import {ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MapElement} from '../../map-element.model';
import {Geometry} from 'geojson';
import {Subject} from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import {DOCUMENT} from '@angular/common';
import {BaseComponent} from '../../../../components/general/base/base.component';
import {OverlayPanelComponent} from '../../../../components/overlay-panel/overlay-panel.component';
import {AlarmService} from '../../../../alarm/alarm.service';
import {AlarmMessage} from '../../../../alarm/alarm-message';
import { MapService } from '../../map.service';
import { _ } from '~app/shared/utils';

/**
 * Hover component. Displays popup when cursor is placed on top of train. Popup shows speed, estimated arrival time and delays.
 */
@Component({
  selector: 'qm-hover',
  templateUrl: './hover.component.html',
  styleUrls: ['./hover.component.scss']
})
export class HoverComponent extends BaseComponent implements OnInit, OnDestroy {
  /**
   * Current popup html element
   */
  @ViewChild('content') content: ElementRef;
  /**
   * Result overlay reference
   */
  @ViewChild('hoverOverlay', { static: true })
  private hoverOverlay: OverlayPanelComponent;

  public selectedFeature: MapElement<Geometry>;
  public utilId: string | undefined = undefined;
  public alarms: AlarmMessage[] = [];

  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<void> = new Subject();
  private target: HTMLElement;

  // used to maintain hover state when the info button is clicked
  public ignoreNextHoverHide = false;

  constructor(private changeDetector: ChangeDetectorRef,
              private mapService: MapService,
              private alarmService: AlarmService,
              @Inject(DOCUMENT) private document: Document) {
    super();
    this.name = 'HoverComponent';
  }

  /**
   * Angular event, run when view is initialized (dom is rendered etc.)
   */
  ngOnInit() {
    this.mapService.visibleMarkers$.pipe(
      takeUntil(this.ngUnsubscribe),
    ).subscribe(markers => {
      if (this.selectedFeature) {
        const marker = markers.find(m => {
          if (m.id === this.selectedFeature.id) {
            return true;
          }
        });
        if (marker) {
          this.selectedFeature = marker;
        }
      }
    });

    this.mapService.hoverFeature$.pipe(
      filter(feature => !!feature?.data),
      switchMap(feature => this.alarmService.alarmsForFeature$(feature.data)),
      takeUntil(this.ngUnsubscribe),
    ).subscribe(alarms => {
      this.alarms = alarms;
      this.changeDetector.markForCheck();
    });

    this.mapService.hoverFeature$.pipe(
      takeUntil(this.ngUnsubscribe),
    ).subscribe(feature => {
      console.log('Hover change from', this.selectedFeature, 'to', feature);
      if (feature) {
        this.selectedFeature = feature.data;
        this.utilId = feature.event.utilId;

        if (feature.event.elementId) {
          if (this.document.getElementById(feature.event.elementId)) {
            const rect = this.document.getElementById(feature.event.elementId).getBoundingClientRect();
            this.target = {
              offsetHeight: 14,
              offsetWidth: 33,
              getBoundingClientRect: () => {
                return {
                  top: rect.top + rect.height / 2,
                  left: rect.left + rect.width / 2 + 6,
                  right: 200
                };
              }
            } as HTMLElement;
            this.hoverOverlay.show({} as Event, this.target);
            this.changeDetector.markForCheck();
          }
        } else {
          this.target = {
            offsetHeight: 10,
            offsetWidth: 10,
            getBoundingClientRect: () => {
              return {
                top: feature.event.originalEvent.clientY,
                left: feature.event.originalEvent.clientX + 6,
                right: 200
              };
            }
          } as HTMLElement;
          this.hoverOverlay.show({} as Event, this.target);
          this.changeDetector.markForCheck();
        }
      } else if (this.selectedFeature) {
        this.hoverOverlay.hide();
      }
    });
  }

  /**
   * Angular destructor function
   */
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /*public async check() {
    console.log('hmm alarms', this.selectedFeature);
    // const query = 'payload.p_deviceid=' + this.selectedFeature.properties.tripNumber + '&sort=-activatedAt';
    // this.alarms = await this.alarmService.queryAlarms(query);
    console.log('hmm alarms', this.alarms);
  }*/

  public getColor() {
    try {
      if (this.selectedFeature.properties.markerContainerClass.includes('success')) {
        return window.getComputedStyle(document.body).getPropertyValue('--color-success-base');
      } else if (this.selectedFeature.properties.markerContainerClass.includes('warning')) {
        return window.getComputedStyle(document.body).getPropertyValue('--color-warning-base');
      } else if (this.selectedFeature.properties.markerContainerClass.includes('danger')) {
        return window.getComputedStyle(document.body).getPropertyValue('--color-danger-base');
      }
    } catch (e) {
    }
    return window.getComputedStyle(document.body).getPropertyValue('--color-info-base');
  }

  public getTitle() {
    try {
      // TODO: Some kind of generic system to handle this instead of special casing flags, see also `BaseLayer` constructor
      if (this.selectedFeature.layer.id === 'flagLayer' && this.selectedFeature.properties.targetObjectType === 'flag') {
        const layer = this.mapService.getLayer(this.selectedFeature.properties.layerId);
        if (layer) {
          return _('Flag on map layer ') + layer.name;
        }
      }

      return (
        this.selectedFeature.properties[this.selectedFeature.layer.metadata.labelBy] ||
        this.selectedFeature.properties[this.selectedFeature.layer.metadata.uniqueBy] ||
        this.selectedFeature.properties.id
      );
    } catch (error) {
      return '';
    }
  }

  public beforeHoverHide(): void {
    if (this.ignoreNextHoverHide) {
      this.ignoreNextHoverHide = false;
      return;
    }
    this.selectedFeature = null; // setting this to null first prevents an infinite loop from MapService
    this.mapService.changeHoverFeature(null);
    this.changeDetector.detectChanges();
  }

  getAlarmMessage = (alarm: AlarmMessage): string => {
    return this.alarmService.getTranslatedAlarm(alarm.metadata, false);
  }
}
